import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, Filter, Clock, Calendar, TrendingUp, Star } from 'lucide-react';
import LazyMovieCard from './LazyMovieCard';

const SearchModal = ({ isOpen, onClose, movies, onPlay }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('All');
  const [selectedYear, setSelectedYear] = useState('All');
  const [sortBy, setSortBy] = useState('relevance');
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);

  // Get unique genres and years from movies
  const genres = useMemo(() => {
    const genreSet = new Set();
    movies.forEach(movie => {
      const title = movie.title.toLowerCase();
      const description = movie.description.toLowerCase();
      
      if (title.includes('romance') || title.includes('love') || title.includes('wedding') || 
          description.includes('romance') || description.includes('love')) {
        genreSet.add('Romance');
      }
      if (title.includes('christmas') || title.includes('xmas') || title.includes('holiday') || 
          title.includes('festive') || title.includes('santa') || title.includes('nativity') ||
          description.includes('christmas') || description.includes('xmas') || 
          description.includes('holiday') || description.includes('festive') ||
          description.includes('santa') || description.includes('nativity')) {
        genreSet.add('Christmas');
      }
      if (title.includes('comedy') || title.includes('funny') || title.includes('laugh') || 
          description.includes('comedy') || description.includes('funny')) {
        genreSet.add('Comedy');
      }
      if (title.includes('drama') || title.includes('story') || title.includes('life') || 
          description.includes('drama') || description.includes('story')) {
        genreSet.add('Drama');
      }
    });
    return ['All', ...Array.from(genreSet)];
  }, [movies]);

  const years = useMemo(() => {
    const yearSet = new Set();
    movies.forEach(movie => {
      yearSet.add(movie.year);
    });
    return ['All', ...Array.from(yearSet).sort((a, b) => b - a)];
  }, [movies]);

  // Filter and sort movies
  const filteredMovies = useMemo(() => {
    let filtered = movies.filter(movie => {
      const hasSearchQuery = searchQuery.trim().length > 0;
      const hasFilters = selectedGenre !== 'All' || selectedYear !== 'All';
      
      // If no search query and no filters, don't show any movies
      if (!hasSearchQuery && !hasFilters) {
        return false;
      }
      
      // Check search match - more comprehensive search
      const searchTerm = searchQuery.toLowerCase();
      const matchesSearch = !hasSearchQuery || 
        movie.title.toLowerCase().includes(searchTerm) ||
        movie.description.toLowerCase().includes(searchTerm) ||
        (movie.channelName && movie.channelName.toLowerCase().includes(searchTerm)) ||
        (movie.channel_name && movie.channel_name.toLowerCase().includes(searchTerm)) ||
        // Also search in individual words
        movie.title.toLowerCase().split(' ').some(word => word.includes(searchTerm)) ||
        movie.description.toLowerCase().split(' ').some(word => word.includes(searchTerm));
      
      // Check genre match
      const matchesGenre = selectedGenre === 'All' || 
        (selectedGenre === 'Romance' && (
          movie.title.toLowerCase().includes('love') || 
          movie.title.toLowerCase().includes('romance') ||
          movie.description.toLowerCase().includes('love') ||
          movie.description.toLowerCase().includes('romance')
        )) ||
        (selectedGenre === 'Christmas' && (
          movie.title.toLowerCase().includes('christmas') || 
          movie.title.toLowerCase().includes('xmas') ||
          movie.title.toLowerCase().includes('holiday') ||
          movie.title.toLowerCase().includes('festive') ||
          movie.title.toLowerCase().includes('santa') ||
          movie.title.toLowerCase().includes('nativity') ||
          movie.description.toLowerCase().includes('christmas') ||
          movie.description.toLowerCase().includes('xmas') ||
          movie.description.toLowerCase().includes('holiday') ||
          movie.description.toLowerCase().includes('festive') ||
          movie.description.toLowerCase().includes('santa') ||
          movie.description.toLowerCase().includes('nativity')
        )) ||
        (selectedGenre === 'Comedy' && (
          movie.title.toLowerCase().includes('comedy') ||
          movie.description.toLowerCase().includes('comedy')
        )) ||
        (selectedGenre === 'Drama' && (
          movie.title.toLowerCase().includes('drama') ||
          movie.description.toLowerCase().includes('drama')
        ));
      
      // Check year match
      const matchesYear = selectedYear === 'All' || movie.year.toString() === selectedYear;
      
      return matchesSearch && matchesGenre && matchesYear;
    });

    // Sort movies
    switch (sortBy) {
      case 'relevance':
        // Keep original order for relevance
        break;
      case 'year':
        filtered.sort((a, b) => b.year - a.year);
        break;
      case 'views':
        filtered.sort((a, b) => {
          const aViews = parseInt(a.views.replace(/[KM]/g, '')) * (a.views.includes('M') ? 1000000 : 1000);
          const bViews = parseInt(b.views.replace(/[KM]/g, '')) * (b.views.includes('M') ? 1000000 : 1000);
          return bViews - aViews;
        });
        break;
      case 'title':
        filtered.sort((a, b) => a.title.localeCompare(b.title));
        break;
      default:
        break;
    }

    return filtered;
  }, [movies, searchQuery, selectedGenre, selectedYear, sortBy]);

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('noilio_recent_searches');
    if (saved) {
      setRecentSearches(JSON.parse(saved));
    }
  }, []);

  // Save search to recent searches
  const saveToRecentSearches = (query) => {
    if (!query.trim()) return;
    
    const updated = [query, ...recentSearches.filter(s => s !== query)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('noilio_recent_searches', JSON.stringify(updated));
  };

  // Generate search suggestions
  const searchSuggestions = useMemo(() => {
    if (!searchQuery.trim() || searchQuery.length < 2) return [];
    
    const query = searchQuery.toLowerCase();
    const suggestions = new Set();
    
    // Add movie titles that match
    movies.forEach(movie => {
      if (movie.title.toLowerCase().includes(query)) {
        suggestions.add(movie.title);
      }
      // Add individual words from titles
      movie.title.toLowerCase().split(' ').forEach(word => {
        if (word.includes(query) && word.length > 2) {
          suggestions.add(word);
        }
      });
    });
    
    // Add recent searches that match
    recentSearches.forEach(search => {
      if (search.toLowerCase().includes(query)) {
        suggestions.add(search);
      }
    });
    
    return Array.from(suggestions).slice(0, 8);
  }, [searchQuery, movies, recentSearches]);

  // Reset filters when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setSelectedGenre('All');
      setSelectedYear('All');
      setSortBy('relevance');
      setOpenDropdown(null);
      setShowSuggestions(false);
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest('.dropdown-container')) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  // Handle dropdown toggle
  const toggleDropdown = (dropdown) => {
    setOpenDropdown(openDropdown === dropdown ? null : dropdown);
  };

  // Handle option selection
  const handleOptionSelect = (type, value) => {
    switch (type) {
      case 'genre':
        setSelectedGenre(value);
        break;
      case 'year':
        setSelectedYear(value);
        break;
      case 'sort':
        setSortBy(value);
        break;
      default:
        // Handle any unexpected type
        break;
    }
    setOpenDropdown(null);
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion);
    saveToRecentSearches(suggestion);
    setShowSuggestions(false);
  };

  const handleInputChange = (e) => {
    setSearchQuery(e.target.value);
    setShowSuggestions(e.target.value.length > 0);
  };

  // Get display labels
  const getGenreLabel = () => selectedGenre === 'All' ? 'All Genre' : selectedGenre;
  const getYearLabel = () => selectedYear === 'All' ? 'All Years' : selectedYear;
  const getSortLabel = () => {
    switch (sortBy) {
      case 'relevance': return 'Relevance';
      case 'year': return 'New to Old';
      case 'views': return 'Most Watched';
      case 'title': return 'A-Z';
      default: return 'Relevance';
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/95 backdrop-blur-sm z-50 overflow-y-auto"
      >
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-white">Search Movies</h1>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <X className="w-8 h-8 text-white" />
            </button>
          </div>

          {/* Search Bar */}
          <div className="relative mb-8">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-6 h-6 text-white/60" />
            <input
              type="text"
              placeholder="Search for movies, genres, actors, channels..."
              value={searchQuery}
              onChange={handleInputChange}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              className="w-full bg-black/40 backdrop-blur-md border border-white/20 rounded-xl px-12 py-4 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-primary-purple/50 focus:border-primary-purple transition-all duration-300 hover:bg-black/50 text-lg"
              autoFocus
            />
            
            {/* Search Suggestions Dropdown */}
            {showSuggestions && (searchSuggestions.length > 0 || recentSearches.length > 0) && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-black/90 backdrop-blur-md border border-white/20 rounded-xl overflow-hidden z-50">
                {searchSuggestions.length > 0 && (
                  <div className="p-2">
                    <div className="text-xs text-white/60 px-3 py-2 font-medium">Suggestions</div>
                    {searchSuggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="w-full text-left px-3 py-2 text-white hover:bg-white/10 rounded-lg transition-colors flex items-center gap-2"
                      >
                        <Search className="w-4 h-4 text-white/40" />
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
                
                {recentSearches.length > 0 && searchQuery.length === 0 && (
                  <div className="p-2 border-t border-white/10">
                    <div className="text-xs text-white/60 px-3 py-2 font-medium">Recent Searches</div>
                    {recentSearches.map((search, index) => (
                      <button
                        key={index}
                        onClick={() => handleSuggestionClick(search)}
                        className="w-full text-left px-3 py-2 text-white hover:bg-white/10 rounded-lg transition-colors flex items-center gap-2"
                      >
                        <Clock className="w-4 h-4 text-white/40" />
                        {search}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Mobile Filter Row */}
          <div className="md:hidden mb-6">
            <div className="flex items-center gap-2">
              {/* Genre Filter */}
              <div className="flex-1 relative dropdown-container">
                <button
                  onClick={() => toggleDropdown('genre')}
                  className="w-full bg-black/60 backdrop-blur-md border border-white/30 rounded-lg px-3 py-2.5 text-white text-xs hover:bg-black/70 transition-all duration-300 flex items-center justify-between"
                >
                  <span>{getGenreLabel()}</span>
                  <svg className="w-3 h-3 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {openDropdown === 'genre' && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-black/90 backdrop-blur-md border border-white/20 rounded-lg shadow-lg z-50">
                    {genres.map(genre => (
                      <button
                        key={genre}
                        onClick={() => handleOptionSelect('genre', genre)}
                        className={`w-full px-3 py-2 text-left text-xs transition-colors first:rounded-t-lg last:rounded-b-lg ${
                          selectedGenre === genre 
                            ? 'bg-primary-purple/20 text-primary-purple' 
                            : 'text-white hover:bg-white/10'
                        }`}
                      >
                        {genre === 'All' ? 'All Genre' : genre}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Year Filter */}
              <div className="flex-1 relative dropdown-container">
                <button
                  onClick={() => toggleDropdown('year')}
                  className="w-full bg-black/60 backdrop-blur-md border border-white/30 rounded-lg px-3 py-2.5 text-white text-xs hover:bg-black/70 transition-all duration-300 flex items-center justify-between"
                >
                  <span>{getYearLabel()}</span>
                  <svg className="w-3 h-3 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {openDropdown === 'year' && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-black/90 backdrop-blur-md border border-white/20 rounded-lg shadow-lg z-50">
                    {years.map(year => (
                      <button
                        key={year}
                        onClick={() => handleOptionSelect('year', year)}
                        className={`w-full px-3 py-2 text-left text-xs transition-colors first:rounded-t-lg last:rounded-b-lg ${
                          selectedYear === year 
                            ? 'bg-primary-purple/20 text-primary-purple' 
                            : 'text-white hover:bg-white/10'
                        }`}
                      >
                        {year === 'All' ? 'All Years' : year}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Sort Filter */}
              <div className="flex-1 relative dropdown-container">
                <button
                  onClick={() => toggleDropdown('sort')}
                  className="w-full bg-black/60 backdrop-blur-md border border-white/30 rounded-lg px-3 py-2.5 text-white text-xs hover:bg-black/70 transition-all duration-300 flex items-center justify-between"
                >
                  <span>{getSortLabel()}</span>
                  <svg className="w-3 h-3 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {openDropdown === 'sort' && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-black/90 backdrop-blur-md border border-white/20 rounded-lg shadow-lg z-50">
                    <button
                      onClick={() => handleOptionSelect('sort', 'relevance')}
                      className={`w-full px-3 py-2 text-left text-xs transition-colors first:rounded-t-lg last:rounded-b-lg ${
                        sortBy === 'relevance' 
                          ? 'bg-primary-purple/20 text-primary-purple' 
                          : 'text-white hover:bg-white/10'
                      }`}
                    >
                      Default
                    </button>
                    <button
                      onClick={() => handleOptionSelect('sort', 'year')}
                      className={`w-full px-3 py-2 text-left text-xs transition-colors first:rounded-t-lg last:rounded-b-lg ${
                        sortBy === 'year' 
                          ? 'bg-primary-purple/20 text-primary-purple' 
                          : 'text-white hover:bg-white/10'
                      }`}
                    >
                      New to Old
                    </button>
                    <button
                      onClick={() => handleOptionSelect('sort', 'views')}
                      className={`w-full px-3 py-2 text-left text-xs transition-colors first:rounded-t-lg last:rounded-b-lg ${
                        sortBy === 'views' 
                          ? 'bg-primary-purple/20 text-primary-purple' 
                          : 'text-white hover:bg-white/10'
                      }`}
                    >
                      Most Watched
                    </button>
                    <button
                      onClick={() => handleOptionSelect('sort', 'title')}
                      className={`w-full px-3 py-2 text-left text-xs transition-colors first:rounded-t-lg last:rounded-b-lg ${
                        sortBy === 'title' 
                          ? 'bg-primary-purple/20 text-primary-purple' 
                          : 'text-white hover:bg-white/10'
                      }`}
                    >
                      A-Z
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Desktop Filters */}
          <div className="hidden md:grid grid-cols-3 gap-6 mb-8">
            {/* Genre Filter */}
            <div>
              <label className="block text-white/90 text-sm font-semibold mb-3 flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Genre
              </label>
              <select
                value={selectedGenre}
                onChange={(e) => setSelectedGenre(e.target.value)}
                className="w-full bg-black/40 backdrop-blur-md border border-white/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-primary-purple/50 focus:border-primary-purple transition-all duration-300 hover:bg-black/50 appearance-none cursor-pointer"
              >
                {genres.map(genre => (
                  <option key={genre} value={genre} className="bg-gray-900 text-white">{genre}</option>
                ))}
              </select>
            </div>

            {/* Year Filter */}
            <div>
              <label className="block text-white/90 text-sm font-semibold mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Year
              </label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="w-full bg-black/40 backdrop-blur-md border border-white/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-primary-purple/50 focus:border-primary-purple transition-all duration-300 hover:bg-black/50 appearance-none cursor-pointer"
              >
                {years.map(year => (
                  <option key={year} value={year} className="bg-gray-900 text-white">{year}</option>
                ))}
              </select>
            </div>

            {/* Sort Filter */}
            <div>
              <label className="block text-white/90 text-sm font-semibold mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Sort By
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full bg-black/40 backdrop-blur-md border border-white/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-primary-purple/50 focus:border-primary-purple transition-all duration-300 hover:bg-black/50 appearance-none cursor-pointer"
              >
                <option value="relevance" className="bg-gray-900 text-white">Relevance</option>
                <option value="year" className="bg-gray-900 text-white">Year (Newest)</option>
                <option value="views" className="bg-gray-900 text-white">Most Watched</option>
                <option value="title" className="bg-gray-900 text-white">Title (A-Z)</option>
              </select>
            </div>
          </div>

          {/* Results */}
          <div className="mb-6">
            <div className="bg-black/20 backdrop-blur-sm border border-white/10 rounded-xl px-4 py-3">
              <p className="text-white/90 font-medium">
                {filteredMovies.length} movie{filteredMovies.length !== 1 ? 's' : ''} found
                {searchQuery && (
                  <span className="text-primary-gold"> for "{searchQuery}"</span>
                )}
              </p>
            </div>
          </div>


          {/* Movie Grid */}
          {filteredMovies.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
              {filteredMovies.map((movie, index) => {
                // Load first 8 cards immediately (above the fold for modal)
                const shouldLoadImmediately = index < 8;
                
                return (
                  <LazyMovieCard
                    key={movie.id}
                    video={movie}
                    interactionMode="modal"
                    onPlay={onPlay}
                    className="w-full"
                    shouldLoadImmediately={shouldLoadImmediately}
                  />
                );
              })}
            </div>
          ) : searchQuery.trim() ? (
            <div className="text-center py-20">
              <div className="bg-black/20 backdrop-blur-sm border border-white/10 rounded-2xl p-12 max-w-3xl mx-auto">
                <Search className="w-20 h-20 text-white/40 mx-auto mb-6" />
                <h3 className="text-2xl text-white/80 mb-3 font-semibold">
                  No results found for <span className="text-primary-gold">"{searchQuery}"</span>
                </h3>
                <p className="text-white/60 mb-8">
                  Try different keywords, check your spelling, or browse our popular movies below
                </p>
                
                {/* Popular Movies Suggestions */}
                <div className="mb-8">
                  <h4 className="text-lg text-white/90 mb-6 font-medium flex items-center gap-2">
                    <Star className="w-5 h-5 text-primary-gold" />
                    Popular Movies
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    {movies.slice(0, 8).map((movie) => (
                      <LazyMovieCard
                        key={movie.id}
                        video={movie}
                        interactionMode="modal"
                        onPlay={onPlay}
                        className="w-full"
                        shouldLoadImmediately={true}
                      />
                    ))}
                  </div>
                </div>
                
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedGenre('All');
                      setSelectedYear('All');
                      setSortBy('relevance');
                    }}
                    className="bg-primary-purple/20 hover:bg-primary-purple/30 border border-primary-purple/50 text-primary-purple px-6 py-2 rounded-lg transition-colors"
                  >
                    Clear Search
                  </button>
                  <button
                    onClick={() => {
                      setSelectedGenre('All');
                      setSelectedYear('All');
                      setSortBy('relevance');
                    }}
                    className="bg-white/10 hover:bg-white/20 border border-white/20 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    Reset Filters
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="bg-black/20 backdrop-blur-sm border border-white/10 rounded-2xl p-12 max-w-4xl mx-auto">
                <h3 className="text-2xl text-white/80 mb-3 font-semibold">Start Exploring</h3>
                <p className="text-white/60 mb-8">
                  Search for movies, browse by genre, or check out our trending collections
                </p>
                
                {/* Trending Movies */}
                <div className="mb-8">
                  <h4 className="text-lg text-white/90 mb-6 font-medium flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-primary-purple" />
                    Trending Now
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    {movies.slice(0, 8).map((movie) => (
                      <LazyMovieCard
                        key={movie.id}
                        video={movie}
                        interactionMode="modal"
                        onPlay={onPlay}
                        className="w-full"
                        shouldLoadImmediately={true}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SearchModal;
